import { AnimatePresence, motion } from "framer-motion";
import { BedDouble, Building2, User, Key, Edit2, Trash2, UserPlus, Shield, Volume2, Briefcase, Settings as SettingsIcon, Link2 } from "lucide-react";
import React, { useState, useEffect } from "react";
import { SectorManagement } from "../components/settings/SectorManagement";
import { BedManagementSettings } from "../components/settings/BedManagementSettings";
import { ContractManagement } from "../components/settings/ContractManagement";
import WorkflowSettings from "../components/settings/WorkflowSettings";
import IntegrationSettings from "../components/settings/IntegrationSettings";
import GeneralSettings from "../components/settings/GeneralSettings"; // [Novo]
import SoundManager from "../components/SoundManager";
import UserFormModal from "../components/UserFormModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import JobRoleModal from "../components/JobRoleModal";
import api from "../services/api";

const Settings = () => {
    const [activeTab, setActiveTab] = useState("general"); // [Novo] Default to general

    // User Management State
    const [users, setUsers] = useState([]);
    const [jobRoles, setJobRoles] = useState([]);
    const [isUserModalOpen, setUserModalOpen] = useState(false);
    const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
    const [isJobRoleModalOpen, setJobRoleModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    const fetchUsersData = async () => {
        setIsLoadingUsers(true);
        try {
            const [usersRes, jobRolesRes] = await Promise.all([
                api.get("/users"),
                api.get("/job-roles")
            ]);
            setUsers(usersRes.data);
            setJobRoles(jobRolesRes.data);
        } catch (error) {
            console.error("Erro ao carregar dados de usuários:", error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (activeTab === "users") {
            fetchUsersData();
        }
    }, [activeTab]);

    const handleDeactivateUser = async (userId) => {
        if (window.confirm("Tem certeza que deseja desativar este usuário?")) {
            try {
                await api.post(`/users/${userId}/deactivate`);
                fetchUsersData();
            } catch (error) {
                console.error("Erro ao desativar:", error);
                alert("Erro ao desativar usuário");
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50/50 dark:bg-black/20 p-4 md:p-10 pb-32">
            <div className="w-full max-w-6xl mx-auto space-y-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Configurações</h1>
                    <p className="text-gray-500 dark:text-gray-400">Gerencie setores, leitos e usuários do sistema.</p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800 pb-1">
                    <button
                        onClick={() => setActiveTab("sectors")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "sectors"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <Building2 size={18} />
                        Setores
                    </button>
                    <button
                        onClick={() => setActiveTab("beds")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "beds"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <BedDouble size={18} />
                        Leitos
                    </button>
                    <button
                        onClick={() => setActiveTab("general")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "general"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <SettingsIcon size={18} />
                        Geral
                    </button>
                    <button
                        onClick={() => setActiveTab("users")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "users"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <User size={18} />
                        Usuários
                    </button>
                    <button
                        onClick={() => setActiveTab("contracts")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "contracts"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <Briefcase size={18} />
                        Contratos
                    </button>
                    <button
                        onClick={() => setActiveTab("sounds")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "sounds"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <Volume2 size={18} />
                        Sons
                    </button>
                    <button
                        onClick={() => setActiveTab("workflows")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "workflows"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <SettingsIcon size={18} />
                        Fluxos
                    </button>
                    <button
                        onClick={() => setActiveTab("integrations")}
                        className={`px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-all relative ${activeTab === "integrations"
                            ? "text-blue-600 bg-white dark:bg-gray-900 border-x border-t border-gray-200 dark:border-gray-800 bottom-[-1px] z-10"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
                            }`}
                    >
                        <Link2 size={18} />
                        Integrações
                    </button>
                </div>

                {/* Content */}
                <div className="bg-white dark:bg-gray-900 rounded-b-xl rounded-tr-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm min-h-[825px]">
                    <AnimatePresence mode="wait">
                        {activeTab === "sectors" && (
                            <motion.div
                                key="sectors"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <SectorManagement />
                            </motion.div>
                        )}
                        {activeTab === "beds" && (
                            <motion.div
                                key="beds"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <BedManagementSettings />
                            </motion.div>
                        )}
                        {activeTab === "users" && (
                            <motion.div
                                key="users"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="space-y-8">
                                    {/* Gerenciamento de Cargos */}
                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-100 dark:border-gray-800">
                                        <div className="flex justify-between items-center mb-4">
                                            <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                                <Shield className="w-5 h-5" />
                                                Cargos e Permissões
                                            </h2>
                                            <button
                                                onClick={() => setJobRoleModalOpen(true)}
                                                className="bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition flex items-center gap-2 text-sm"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Novo Cargo
                                            </button>
                                        </div>

                                        <div className="flex gap-2 flex-wrap">
                                            {jobRoles.map(role => (
                                                <button
                                                    key={role.id}
                                                    onClick={() => {
                                                        setSelectedRole(role);
                                                        setJobRoleModalOpen(true);
                                                    }}
                                                    className="bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 px-3 py-1 rounded-full text-sm text-gray-700 dark:text-gray-300 transition-colors cursor-pointer"
                                                >
                                                    {role.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Gerenciamento de Usuários */}
                                    <div>
                                        <div className="flex justify-between items-center mb-4">
                                            <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                                <UserPlus className="w-5 h-5" />
                                                Usuários Cadastrados
                                            </h2>
                                            <button
                                                onClick={() => {
                                                    setSelectedUser(null);
                                                    setUserModalOpen(true);
                                                }}
                                                className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 text-sm"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Novo Usuário
                                            </button>
                                        </div>

                                        <div className="overflow-x-auto pb-2">
                                            <table className="w-full text-left border-collapse min-w-[600px]">
                                                <thead className="border-b border-gray-200 dark:border-gray-800">
                                                    <tr>
                                                        <th className="p-3 font-medium text-gray-500 dark:text-gray-400">Nome</th>
                                                        <th className="p-3 font-medium text-gray-500 dark:text-gray-400">Email</th>
                                                        <th className="p-3 font-medium text-gray-500 dark:text-gray-400">Cargo</th>
                                                        <th className="p-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                                                        <th className="p-3 font-medium text-gray-500 dark:text-gray-400 text-right">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {users.map((u) => (
                                                        <tr key={u.id} className="border-b border-gray-100 dark:border-gray-800/50 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                                            <td className="p-3 text-gray-800 dark:text-white">{u.full_name}</td>
                                                            <td className="p-3 text-gray-600 dark:text-gray-400">{u.email}</td>
                                                            <td className="p-3">
                                                                <span className="bg-blue-50 text-blue-700 border border-blue-100 text-xs px-2 py-1 rounded dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-900/50">
                                                                    {u.role_name || 'Sem cargo'}
                                                                </span>
                                                            </td>
                                                            <td className="p-3">
                                                                <span className={`text-xs px-2 py-1 rounded border ${u.is_active ? 'bg-green-50 text-green-700 border-green-100 dark:bg-green-900/30 dark:text-green-300 dark:border-green-900/50' : 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/50'}`}>
                                                                    {u.is_active ? 'Ativo' : 'Inativo'}
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-right flex justify-end gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedUser(u);
                                                                        setPasswordModalOpen(true);
                                                                    }}
                                                                    className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                                                                    title="Alterar Senha"
                                                                >
                                                                    <Key className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedUser(u);
                                                                        setUserModalOpen(true);
                                                                    }}
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                    title="Editar"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                                {u.is_active && (
                                                                    <button
                                                                        onClick={() => handleDeactivateUser(u.id)}
                                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                        title="Desativar"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                        {activeTab === "contracts" && (
                            <motion.div
                                key="contracts"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <ContractManagement />
                            </motion.div>
                        )}
                        {activeTab === "sounds" && (
                            <motion.div
                                key="sounds"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <SoundManager />
                            </motion.div>
                        )}
                        {activeTab === "workflows" && (
                            <motion.div
                                key="workflows"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <WorkflowSettings />
                            </motion.div>
                        )}
                        {activeTab === "integrations" && (
                            <motion.div
                                key="integrations"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <IntegrationSettings />
                            </motion.div>
                        )}
                        {activeTab === "general" && (
                            <motion.div
                                key="general"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <GeneralSettings />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Modals */}
            {
                isUserModalOpen && (
                    <UserFormModal
                        isOpen={isUserModalOpen}
                        onClose={() => setUserModalOpen(false)}
                        user={selectedUser}
                        onSuccess={fetchUsersData}
                        jobRoles={jobRoles}
                    />
                )
            }

            {
                isPasswordModalOpen && (
                    <ChangePasswordModal
                        isOpen={isPasswordModalOpen}
                        onClose={() => setPasswordModalOpen(false)}
                        userId={selectedUser?.id}
                    />
                )
            }

            {
                isJobRoleModalOpen && (
                    <JobRoleModal
                        show={isJobRoleModalOpen}
                        onClose={() => {
                            setJobRoleModalOpen(false);
                            setSelectedRole(null);
                        }}
                        onSave={fetchUsersData}
                        editRole={selectedRole}
                    />
                )
            }
        </div >
    );
};

export default Settings;
function Plus({ className }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
        </svg>
    )
}
